import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  Stack,
  LinearProgress,
  Avatar,
  Tooltip,
} from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { ControlCenterRoundContainer } from "./ControlCenterComponents";
import { debounce, isNumber } from "lodash";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ScoreboardIcon from "@mui/icons-material/Scoreboard";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import { getClosestGuessBonusRoundScoreDB } from "../../utils/utils";
import { StandardButton } from "../../components/StandardButton";
import { Tables } from "../../../database.types";
import { gameService } from "../../data/gameService";
import { useSupabase } from "../../auth/useSupabase";

interface ClosestGuessBonusRoundAdminProps {
  bonusRound: Tables<"bonus_rounds">;
  closestGuessQuestions: Tables<"closest_guess_questions">[];
  team1Name: string;
  team2Name: string;
  gameId: string;
  gameState: Tables<"game_state">;
  refetchData: () => Promise<void>;
}

const ClosestGuessBonusRoundAdmin = (
  props: ClosestGuessBonusRoundAdminProps,
) => {
  const {
    bonusRound,
    closestGuessQuestions,
    team1Name,
    team2Name,
    gameId,
    gameState,
    refetchData,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { supabase } = useSupabase();

  const [actionLoading, setActionLoading] = useState(false);
  const loadingCountRef = useRef(0);

  const startLoading = useCallback(() => {
    loadingCountRef.current++;
    setActionLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
    if (loadingCountRef.current === 0) {
      setActionLoading(false);
    }
  }, []);

  const withLoading = useCallback(
    (fn: () => Promise<void>) => async () => {
      if (actionLoading) return;
      startLoading();
      try {
        await fn();
      } finally {
        stopLoading();
      }
    },
    [actionLoading, startLoading, stopLoading],
  );

  // Sort questions by question_order
  const sortedQuestions = useMemo(
    () =>
      [...closestGuessQuestions].sort(
        (a, b) => a.question_order - b.question_order,
      ),
    [closestGuessQuestions],
  );
  const currentQuestion =
    bonusRound.current_question_idx === null
      ? null
      : sortedQuestions[bonusRound.current_question_idx];

  // Local state for text field responsiveness
  const [localTeam1Answer, setLocalTeam1Answer] = useState<string>(
    currentQuestion?.team_1_answer?.toString() ?? "",
  );
  const [localTeam2Answer, setLocalTeam2Answer] = useState<string>(
    currentQuestion?.team_2_answer?.toString() ?? "",
  );

  // Track which question the local state corresponds to
  const lastQuestionIdRef = useRef<string | null>(currentQuestion?.id ?? null);
  if (currentQuestion && currentQuestion.id !== lastQuestionIdRef.current) {
    lastQuestionIdRef.current = currentQuestion.id;
    setLocalTeam1Answer(currentQuestion.team_1_answer?.toString() ?? "");
    setLocalTeam2Answer(currentQuestion.team_2_answer?.toString() ?? "");
  }

  // Derive scores
  const { team1Score, team2Score } =
    getClosestGuessBonusRoundScoreDB(sortedQuestions);

  const currentRoundIdx: "bonus" | "overtime" =
    gameState.current_phase === "overtime" ? "overtime" : "bonus";

  // Debounced DB write for team answers
  const debouncedUpdateAnswer = useCallback(
    debounce(
      async (
        questionId: string,
        field: "team_1_answer" | "team_2_answer",
        value: number | null,
      ) => {
        startLoading();
        try {
          await gameService.updateClosestGuessQuestion(supabase, questionId, {
            [field]: value,
          });
          await gameService.updateGameState(supabase, gameId, {
            updated_at: new Date().toISOString(),
          });
          await refetchData();
        } finally {
          stopLoading();
        }
      },
      300,
    ),
    [supabase, refetchData, startLoading, stopLoading],
  );

  const handleTeamAnswerChange = (team: 1 | 2, value: string) => {
    if (!currentQuestion) return;
    const numValue = value === "" ? null : Number(value);
    const field = team === 1 ? "team_1_answer" : "team_2_answer";

    if (team === 1) {
      setLocalTeam1Answer(value);
    } else {
      setLocalTeam2Answer(value);
    }

    debouncedUpdateAnswer(currentQuestion.id, field, numValue);
  };

  const handlePreviousQuestion = withLoading(async () => {
    if (bonusRound.current_question_idx === 0) {
      await gameService.updateBonusRound(supabase, bonusRound.id, {
        current_question_idx: null,
        show_answer: false,
      });
    } else if (
      bonusRound.current_question_idx !== null &&
      bonusRound.current_question_idx > 0
    ) {
      await gameService.updateBonusRound(supabase, bonusRound.id, {
        current_question_idx: bonusRound.current_question_idx - 1,
        show_answer: false,
      });
    }
    await gameService.updateGameState(supabase, gameId, {
      updated_at: new Date().toISOString(),
    });
    await refetchData();
  });

  const handleNextQuestion = withLoading(async () => {
    if (bonusRound.current_question_idx !== null) {
      await gameService.updateBonusRound(supabase, bonusRound.id, {
        current_question_idx: bonusRound.current_question_idx + 1,
        show_answer: false,
      });
      await gameService.updateGameState(supabase, gameId, {
        updated_at: new Date().toISOString(),
      });
      await refetchData();
    }
  });

  const handleStartGame = withLoading(async () => {
    await gameService.updateBonusRound(supabase, bonusRound.id, {
      current_question_idx: 0,
      show_answer: false,
    });
    await gameService.updateGameState(supabase, gameId, {
      updated_at: new Date().toISOString(),
    });
    await refetchData();
  });

  const handleToggleShowAnswer = withLoading(async () => {
    await gameService.updateBonusRound(supabase, bonusRound.id, {
      show_answer: !bonusRound.show_answer,
    });
    await gameService.updateGameState(supabase, gameId, {
      updated_at: new Date().toISOString(),
    });
    await refetchData();
  });

  const handleNavigateToRound = async (roundNumber: number) => {
    await gameService.navigateToRound(supabase, gameId, roundNumber);
    await refetchData();
  };

  const curAnswer = currentQuestion?.correct_answer;

  if (!currentQuestion || curAnswer === null || curAnswer === undefined) {
    return (
      <ControlCenterRoundContainer
        onClickNext={() => handleNavigateToRound(6)}
        onClickPrevious={() =>
          handleNavigateToRound(currentRoundIdx === "overtime" ? 12 : 6)
        }
        currentRoundIdx={currentRoundIdx}
      >
        <Paper
          sx={{
            display: "flex",
            p: "32px",
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <StandardButton onClick={handleStartGame} loading={actionLoading}>
            Start Game (Will show first question)
          </StandardButton>
        </Paper>
      </ControlCenterRoundContainer>
    );
  }

  const team1Difference: string = isNumber(currentQuestion.team_1_answer)
    ? Math.abs(currentQuestion.team_1_answer - curAnswer).toString()
    : "N/A";

  const team2Difference: string = isNumber(currentQuestion.team_2_answer)
    ? Math.abs(currentQuestion.team_2_answer - curAnswer).toString()
    : "N/A";

  let winner: 1 | 2 | null = null;
  if (
    team1Difference === "N/A" ||
    team2Difference === "N/A" ||
    team1Difference === team2Difference
  ) {
    winner = null;
  } else if (parseFloat(team1Difference) > parseFloat(team2Difference)) {
    winner = 2;
  } else if (parseFloat(team2Difference) > parseFloat(team1Difference)) {
    winner = 1;
  }

  // Calculate progress percentage
  const progressPercentage =
    (((bonusRound.current_question_idx ?? 0) + 1) / sortedQuestions.length) *
    100;

  return (
    <ControlCenterRoundContainer
      onClickNext={() => handleNavigateToRound(7)}
      onClickPrevious={() =>
        handleNavigateToRound(currentRoundIdx === "overtime" ? 12 : 6)
      }
      currentRoundIdx={currentRoundIdx}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 2,
          borderRadius: 2,
          overflow: "auto",
          width: "900px",
          transition: "box-shadow 0.3s ease-in-out, opacity 0.2s ease",
          opacity: actionLoading ? 0.7 : 1,
          "&:hover": {
            boxShadow: theme.shadows[5],
          },
        }}
      >
        {actionLoading && (
          <LinearProgress
            sx={{
              mb: 2,
              mx: { xs: -2, sm: -3 },
              mt: { xs: -2, sm: -3 },
              borderRadius: "8px 8px 0 0",
            }}
          />
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            color="primary.main"
            sx={{
              fontWeight: 600,
            }}
          >
            Closest Guess Bonus Round
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              borderRadius: 2,
              bgcolor: "background.paper",
              boxShadow: 1,
            }}
          >
            <ScoreboardIcon color="primary" />
            <Typography variant="subtitle1" fontWeight="bold">
              Score:
            </Typography>
            <Chip
              icon={<LooksOneIcon />}
              label={team1Score}
              color="primary"
              variant="outlined"
              size={isMobile ? "small" : "medium"}
            />
            <Typography variant="body2" fontWeight="medium">
              vs
            </Typography>
            <Chip
              icon={<LooksTwoIcon />}
              label={team2Score}
              color="secondary"
              variant="outlined"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          color="secondary"
          sx={{ mb: 2, height: 6, borderRadius: 3 }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <StandardButton
            onClick={handlePreviousQuestion}
            disabled={actionLoading}
            startIcon={<KeyboardArrowLeftIcon />}
            size={isMobile ? "small" : "medium"}
            variant="outlined"
          >
            {!isMobile && "Previous"}
          </StandardButton>

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "medium",
              px: 2,
              py: 1,
              borderRadius: 1,
              backgroundColor: "background.paper",
              boxShadow: 1,
            }}
          >
            {bonusRound.current_question_idx !== null
              ? `Question ${(bonusRound.current_question_idx ?? 0) + 1} of ${sortedQuestions.length}`
              : "Round not started"}
          </Typography>

          <StandardButton
            onClick={handleNextQuestion}
            disabled={
              actionLoading ||
              bonusRound.current_question_idx === sortedQuestions.length - 1
            }
            endIcon={<KeyboardArrowRightIcon />}
            size={isMobile ? "small" : "medium"}
            variant="outlined"
          >
            {!isMobile && "Next"}
          </StandardButton>
        </Box>

        <Card
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: 2,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h6"
              gutterBottom
              color="primary.main"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <QuestionMarkIcon fontSize="small" />
              Question:
            </Typography>
            <Typography
              variant="body1"
              paragraph
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                mb: 3,
              }}
            >
              {currentQuestion.question_text}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="h6"
                color="secondary.main"
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <CheckCircleOutlineIcon fontSize="small" />
                Correct Answer:
              </Typography>
              <Chip
                label={curAnswer}
                color="secondary"
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  height: "auto",
                  py: 0.5,
                }}
              />
            </Box>
          </CardContent>
        </Card>

        <StandardButton
          fullWidth
          color="primary"
          variant={bonusRound.show_answer ? "contained" : "outlined"}
          disabled={
            !currentQuestion.team_1_answer || !currentQuestion.team_2_answer
          }
          loading={actionLoading}
          sx={{ mb: "12px" }}
          onClick={handleToggleShowAnswer}
        >
          {bonusRound.show_answer ? "Hide" : "Show"} Answer
        </StandardButton>

        <Grid container spacing={3} justifyContent="space-between">
          <Grid spacing={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                borderRadius: 2,
                height: "100%",
                transition: "transform 0.2s",
                transform: winner === 1 ? "scale(1.02)" : "scale(1)",
                boxShadow: winner === 1 ? theme.shadows[8] : theme.shadows[1],
                border:
                  winner === 1
                    ? `2px solid ${theme.palette.secondary.main}`
                    : "none",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: winner === 1 ? "secondary.main" : "text.primary",
                    }}
                  >
                    {team1Name}
                  </Typography>
                  {winner === 1 && (
                    <Chip
                      icon={<EmojiEventsIcon />}
                      label="Winner"
                      color="secondary"
                      sx={{ fontWeight: "bold" }}
                    />
                  )}
                </Box>

                <TextField
                  fullWidth
                  label="Team 1 Answer"
                  type="number"
                  value={localTeam1Answer}
                  onChange={(e) => handleTeamAnswerChange(1, e.target.value)}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    sx: { fontWeight: "medium" },
                  }}
                />

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Difference:
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: winner === 1 ? "secondary.main" : "text.primary",
                    }}
                  >
                    {team1Difference}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid spacing={{ xs: 12, md: 6 }}>
            <Card
              sx={{
                borderRadius: 2,
                height: "100%",
                transition: "transform 0.2s",
                transform: winner === 2 ? "scale(1.02)" : "scale(1)",
                boxShadow: winner === 2 ? theme.shadows[8] : theme.shadows[1],
                border:
                  winner === 2
                    ? `2px solid ${theme.palette.secondary.main}`
                    : "none",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: winner === 2 ? "secondary.main" : "text.primary",
                    }}
                  >
                    {team2Name}
                  </Typography>
                  {winner === 2 && (
                    <Chip
                      icon={<EmojiEventsIcon />}
                      label="Winner"
                      color="secondary"
                      sx={{ fontWeight: "bold" }}
                    />
                  )}
                </Box>

                <TextField
                  fullWidth
                  label="Team 2 Answer"
                  type="number"
                  value={localTeam2Answer}
                  onChange={(e) => handleTeamAnswerChange(2, e.target.value)}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    sx: { fontWeight: "medium" },
                  }}
                />

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Difference:
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: winner === 2 ? "secondary.main" : "text.primary",
                    }}
                  >
                    {team2Difference}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {winner !== null && (
          <Card
            sx={{
              mt: 3,
              backgroundColor: theme.palette.success.main,
              borderRadius: 2,
            }}
          >
            <CardContent
              sx={{
                textAlign: "center",
                p: { xs: 1, sm: 2 },
                "&:last-child": { pb: 1 },
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
              >
                <EmojiEventsIcon
                  sx={{ color: "white", fontSize: { xs: 24, sm: 30 } }}
                />
                <Typography
                  variant="h6"
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  {winner === 1 ? team1Name : team2Name} wins this question!
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Question Results Tracker */}
        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ScoreboardIcon color="primary" fontSize="small" />
              Round Results
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {sortedQuestions.map((question, index) => {
                // Determine winner for this question
                let questionWinner: 1 | 2 | null = null;
                const answer = question.correct_answer;

                if (
                  answer !== null &&
                  question.team_1_answer !== null &&
                  question.team_2_answer !== null
                ) {
                  const team1Diff = Math.abs(question.team_1_answer - answer);
                  const team2Diff = Math.abs(question.team_2_answer - answer);

                  if (team1Diff < team2Diff) {
                    questionWinner = 1;
                  } else if (team2Diff < team1Diff) {
                    questionWinner = 2;
                  }
                }

                return (
                  <Tooltip
                    key={question.id}
                    title={`Question ${index + 1}: ${
                      questionWinner === null
                        ? "No winner yet"
                        : `Team ${questionWinner} won`
                    }`}
                    arrow
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        backgroundColor:
                          questionWinner === 1
                            ? theme.palette.primary.main
                            : questionWinner === 2
                              ? theme.palette.secondary.main
                              : "grey.300",
                        cursor: "pointer",
                        border:
                          bonusRound.current_question_idx !== null &&
                          question.question_order ===
                            bonusRound.current_question_idx + 1
                            ? "2px solid black"
                            : "none",
                        fontWeight: "bold",
                        "&:hover": {
                          opacity: 0.9,
                          transform: "scale(1.1)",
                        },
                        transition: "transform 0.2s",
                      }}
                    >
                      {index + 1}
                    </Avatar>
                  </Tooltip>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </ControlCenterRoundContainer>
  );
};

export default ClosestGuessBonusRoundAdmin;
